import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import { apiRequest } from '../lib/api';

interface UserStats {
  tier: string;
  scrapes_used: number;
  scrapes_limit: number;
  period_end: string;
}

export function AccountSettings() {
  const { user } = useAuth();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [stats, setStats] = useState<UserStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);

  useEffect(() => {
    apiRequest<UserStats>('/api/user/stats')
      .then(setStats)
      .catch(() => setStats(null))
      .finally(() => setStatsLoading(false));
  }, []);

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError(null);
    setPasswordSuccess(false);

    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match.');
      return;
    }

    if (newPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters.');
      return;
    }

    if (!supabase) {
      setPasswordError('Authentication is not configured.');
      return;
    }

    setPasswordLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });

      if (error) {
        setPasswordError(error.message);
        return;
      }

      setPasswordSuccess(true);
      setNewPassword('');
      setConfirmPassword('');
    } catch {
      setPasswordError('An unexpected error occurred.');
    } finally {
      setPasswordLoading(false);
    }
  };

  const displayName = user?.email?.split('@')[0] || 'User';
  const tierDisplayName = stats?.tier
    ? stats.tier.charAt(0).toUpperCase() + stats.tier.slice(1)
    : 'Free';
  const isUnlimited = stats?.scrapes_limit === -1;

  return (
    <div className="min-h-screen bg-surface-950">
      <div className="max-w-3xl mx-auto p-6 lg:p-8">
        {/* Back link */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-surface-400 hover:text-white transition-colors mb-8"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to app
        </Link>

        <h1 className="text-3xl font-bold text-white mb-8">Account Settings</h1>

        {/* Profile Section */}
        <section className="card p-6 mb-6">
          <h2 className="text-lg font-semibold text-white mb-4">Profile</h2>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-primary-500 flex items-center justify-center text-white text-xl font-bold">
              {displayName.charAt(0).toUpperCase()}
            </div>
            <div>
              <p className="text-white font-medium">{displayName}</p>
              <p className="text-surface-400 text-sm">{user?.email}</p>
            </div>
          </div>
        </section>

        {/* Subscription Section */}
        <section className="card p-6 mb-6">
          <h2 className="text-lg font-semibold text-white mb-4">Subscription</h2>

          {statsLoading ? (
            <div className="flex justify-center py-4">
              <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : stats ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-surface-300">Current Plan</span>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-500/20 text-primary-300">
                  {tierDisplayName}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-surface-300">Usage This Period</span>
                <span className="text-white">
                  {isUnlimited
                    ? `${stats.scrapes_used} transforms (unlimited)`
                    : `${stats.scrapes_used} / ${stats.scrapes_limit} transforms`
                  }
                </span>
              </div>

              {!isUnlimited && (
                <div className="w-full bg-surface-700 rounded-full h-2">
                  <div
                    className="bg-primary-500 h-2 rounded-full transition-all"
                    style={{ width: `${Math.min((stats.scrapes_used / stats.scrapes_limit) * 100, 100)}%` }}
                  />
                </div>
              )}

              {stats.period_end && (
                <div className="flex items-center justify-between">
                  <span className="text-surface-300">Period Ends</span>
                  <span className="text-surface-400 text-sm">
                    {new Date(stats.period_end).toLocaleDateString()}
                  </span>
                </div>
              )}

              <Link to="/pricing" className="btn-secondary inline-block text-center mt-2">
                View Plans
              </Link>
            </div>
          ) : (
            <p className="text-surface-400">
              Unable to load subscription info.{' '}
              <Link to="/pricing" className="text-primary-400 hover:text-primary-300">
                View plans
              </Link>
            </p>
          )}
        </section>

        {/* Change Password Section */}
        <section className="card p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Change Password</h2>

          <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
            <div>
              <label htmlFor="newPassword" className="block text-sm font-medium text-surface-300 mb-1">
                New Password
              </label>
              <input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={6}
                className="w-full px-3 py-2 bg-surface-800 border border-surface-700 rounded-lg text-white placeholder-surface-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                placeholder="Minimum 6 characters"
              />
            </div>

            <div>
              <label htmlFor="confirmNewPassword" className="block text-sm font-medium text-surface-300 mb-1">
                Confirm New Password
              </label>
              <input
                id="confirmNewPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={6}
                className="w-full px-3 py-2 bg-surface-800 border border-surface-700 rounded-lg text-white placeholder-surface-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                placeholder="Confirm your password"
              />
            </div>

            {passwordError && (
              <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                {passwordError}
              </div>
            )}

            {passwordSuccess && (
              <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg text-green-400 text-sm">
                Password updated successfully!
              </div>
            )}

            <button
              type="submit"
              disabled={passwordLoading}
              className="btn-primary py-2.5 disabled:opacity-50"
            >
              {passwordLoading ? 'Updating...' : 'Update Password'}
            </button>
          </form>
        </section>
      </div>
    </div>
  );
}
